import React from "react"
import { Controller } from "react-hook-form"
import styled from "styled-components"
import { Control, UseFormSetValue } from "react-hook-form/dist/types/form"
import { InputKlarnaCheckoutConfig, KlarnaCheckoutConfig } from "lib/types/generated/graphql-types"
import { FieldErrors } from "react-hook-form/dist/types/errors"
import { Common } from "lib/types"

import { SidebarDivider } from "../../DashboardSales/Shared.styled"

import SingleSelect from "components/Ui/Form/SingleSelect"
import Input from "components/Ui/Form/Input"
import Checkbox from "components/Ui/Form/Checkbox"
import { TooltipIcon } from "components/Ui/TooltipIcon"
import { WithAuth } from "../../Auth"
import { PermissionGroups } from "../../../routes/constants"
import FallbackCheckbox from "../../Ui/Form/FallbackCheckbox"
import FallbackInput from "../../Ui/Form/FallbackInput"

const BaseUrlSelect = styled(SingleSelect)`
  margin-bottom: 2rem;
`

const CheckboxWrapper = styled.div`
  display: flex;
  padding-bottom: 1rem;

  div {
    padding-top: 0;
  }
`

interface ExtendedInputKustomConfig extends InputKlarnaCheckoutConfig {
  id: string
}

type Props = {
  control: Control<ExtendedInputKustomConfig>
  errors: FieldErrors
  setValue: UseFormSetValue<ExtendedInputKustomConfig>
  showId?: boolean
  defaultValues?: KlarnaCheckoutConfig
}

export const KustomCheckoutConfigInputs = ({
  control,
  errors,
  setValue,
  showId = false,
  defaultValues
}: Props) => {
  const updateBaseUrl = (baseUrl: Common.Option) => {
    setValue("baseUrl", baseUrl.value)
  }

  const baseUrlOptions = [{
          value: "https://api.playground.kustom.co/",
          label: "Kustom - test | api.playground.kustom.co"
        },
        {
          value: "https://api.kustom.co/",
          label: "Kustom - prod | api.kustom.co"
        }
      ]

  return (
    <>
      {showId && (
        <>
          <Controller
            name="id"
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                label="Identifier *"
                description="For internal use in Brink"
                placeholder="Aa - Zz, 0 - 9, -_."
                errors={errors}
              />
            )}
            control={control}
            rules={{
              required: "This is a required field",
              pattern: {
                value: /^[A-Za-z0-9_.-]+$/,
                message: "Only characters Aa-Zz and 0-9 are allowed. No spaces"
              }
            }}
            defaultValue=""
          />
          <SidebarDivider />
        </>
      )}
      <WithAuth
        requiredGroups={PermissionGroups.providers.write}
        fallback={
          <FallbackCheckbox
            label={"Kustom shipping assistant"}
            value={defaultValues?.klarnaShippingAssistantEnabled ?? false}
          />
        }
      >
        <CheckboxWrapper>
          <Checkbox
            name="klarnaShippingAssistantEnabled"
            label="Enable Kustom shipping assistant"
            control={control}
            defaultValue={defaultValues?.klarnaShippingAssistantEnabled ?? false}
          />

          <TooltipIcon
            content={
              "Before enabling Kustom Shipping Assistant, ensure that your Kustom Merchant ID is properly configured in the Kustom Merchant Portal and that your shipping selector (Nshift, Ingrid, etc.) is connected. See section `Shipping Assistant` in Kustom Docs or reach out to your technical support at Kustom."
            }
          />
        </CheckboxWrapper>
      </WithAuth>
      <WithAuth
        requiredGroups={PermissionGroups.providers.write}
        fallback={<FallbackInput label={"Kustom merchant ID"} value={defaultValues?.merchantId} />}
      >
        <Controller
          name="merchantId"
          render={({ field }) => (
            <Input {...field} type="text" label="Kustom merchant ID *" errors={errors} />
          )}
          control={control}
          defaultValue={defaultValues?.merchantId ?? ""}
          rules={{
            required: "This is a required field"
          }}
        />
      </WithAuth>
      <WithAuth requiredGroups={PermissionGroups.providers.write}>
        <Controller
          name="username"
          render={({ field }) => (
            <Input {...field} type="text" errors={errors} label="Kustom API username*" />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
          defaultValue=""
        />
      </WithAuth>
      <WithAuth requiredGroups={PermissionGroups.providers.write}>
        <Controller
          name="password"
          render={({ field }) => (
            <Input {...field} type="text" label="Kustom API password *" errors={errors} />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
          defaultValue=""
        />
      </WithAuth>
      <WithAuth requiredGroups={PermissionGroups.providers.write} fallback={<FallbackInput label={"Base URL"} value={defaultValues?.baseUrl}/>}>
        <Controller
          name="baseUrl"
          render={({ field }) => (
            <BaseUrlSelect
              {...field}
              options={baseUrlOptions}
              setValue={updateBaseUrl}
              defaultValue={{
                label:
                  baseUrlOptions.find((option) => option.value === defaultValues?.baseUrl)?.label ??
                  "Select base URL",
                value: defaultValues?.baseUrl ?? ""
              }}
              label="Base URL *"
            />
          )}
          control={control}
          rules={{
            required: "This is a required field"
          }}
        />
      </WithAuth>
    </>
  )
}
